import { useCallback, useEffect, useMemo, useState } from 'react';
import { Session, SignedOutError, signIn } from './auth/session';
import { GitHub } from './github/client';
import { checkAccess, type Viewer } from './github/access';
import { APP_VERSION, REPO } from './config';
import { useInstall } from './pwa/install';
import { useNewVersion } from './pwa/version';
import { Header } from './ui/Header';
import { ErrorScreen, Loading, NoAccess, Setup, Unsupported, VersionBanner } from './ui/screens';
import { FilesView, FolderGone, OtherWindow, ReconnectBanner } from './ui/workspace';
import { useWorkspace } from './workcopy/useWorkspace';
import { PullRequests } from './ui/PullRequests';
import { ReviewPage } from './ui/Review';
import { linkProps, useRoute } from './ui/router';
import type { WorkspaceDeps } from './workcopy/workspace';
import type { InstallState } from './pwa/install';

type Phase =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | { kind: 'signed-out'; expired: boolean }
  | { kind: 'no-access'; viewer: Viewer; reason: 'not-found' | 'read-only' }
  | { kind: 'ready'; viewer: Viewer }
  | { kind: 'error'; message: string };

/** The local-folder feature needs the File System Access API (Chromium only). */
export const browserSupported = () => typeof window !== 'undefined' && 'showDirectoryPicker' in window;

type AppProps = { session?: Session; workspaceDeps?: Partial<WorkspaceDeps> };

export function App({ session: injected, workspaceDeps }: AppProps = {}) {
  const session = useMemo(() => injected ?? new Session(), [injected]);
  const gh = useMemo(() => new GitHub(session), [session]);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const install = useInstall();
  const newVersion = useNewVersion(APP_VERSION);

  const start = useCallback(async (opts: { expired?: boolean } = {}) => {
    if (!browserSupported()) return setPhase({ kind: 'unsupported' });
    setPhase({ kind: 'loading' });
    try {
      if (!(await session.load())) return setPhase({ kind: 'signed-out', expired: opts.expired ?? false });
      const access = await checkAccess(gh, REPO);
      setPhase(access.kind === 'ok' ? { kind: 'ready', viewer: access.viewer } : access);
    } catch (e) {
      if (e instanceof SignedOutError) return setPhase({ kind: 'signed-out', expired: true });
      setPhase({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }, [session, gh]);

  useEffect(() => { void start(); }, [start]);

  // Sign-in errors come back as ?auth_error=… from the server callback.
  const authError = new URLSearchParams(location.search).get('auth_error');

  const signOut = async () => { await session.signOut(); setPhase({ kind: 'signed-out', expired: false }); };
  const switchAccount = async () => { await session.signOut(); signIn({ switchAccount: true }); };

  const viewer = phase.kind === 'ready' || phase.kind === 'no-access' ? phase.viewer : undefined;
  const menu = viewer ? { viewer, onSignOut: () => void signOut(), onSwitchAccount: () => void switchAccount() } : {};

  if (phase.kind === 'ready') {
    return (
      <>
        {newVersion && <VersionBanner onReload={() => location.reload()} />}
        {install.kind === 'standalone'
          ? <WorkArea gh={gh} viewer={phase.viewer} install={install} menu={menu} deps={workspaceDeps} />
          : <><Header {...menu} /><Setup viewer={phase.viewer} install={install} onSignIn={() => signIn()} /></>}
      </>
    );
  }

  return (
    <>
      {newVersion && <VersionBanner onReload={() => location.reload()} />}
      <Header {...menu} />
      {authError && phase.kind === 'signed-out' && (
        <div className="hq-banner" role="alert">Sign-in didn’t complete. Please try again.</div>
      )}
      {phase.kind === 'loading' && <Loading />}
      {phase.kind === 'unsupported' && <Unsupported />}
      {phase.kind === 'signed-out' && <Setup viewer={null} sessionExpired={phase.expired} install={install} onSignIn={() => signIn()} />}
      {phase.kind === 'no-access' && (
        <NoAccess viewer={phase.viewer} reason={phase.reason} onRetry={() => void start()} onSwitchAccount={() => void switchAccount()} />
      )}
      {phase.kind === 'error' && <ErrorScreen message={phase.message} onRetry={() => void start()} />}
    </>
  );
}

/** Signed in, with access, in the installed app: the local folder and everything after it. */
function WorkArea({ gh, viewer, install, menu, deps }: {
  gh: GitHub; viewer: Viewer; install: InstallState;
  menu: Parameters<typeof Header>[0]; deps: Partial<WorkspaceDeps> | undefined;
}) {
  const { ws, phase } = useWorkspace(gh, REPO, deps);
  const route = useRoute();
  const hasFolder = !['none', 'checking'].includes(phase.kind);
  const nav = phase.kind === 'ready' ? (
    <nav className="hq-nav" aria-label="Main">
      <a {...linkProps({ name: 'prs' })} aria-current={route.name !== 'files' ? 'page' : undefined}>Pull requests</a>
      <a {...linkProps({ name: 'files' })} aria-current={route.name === 'files' ? 'page' : undefined}>Your files</a>
    </nav>
  ) : undefined;
  const header = <Header {...menu} nav={nav} {...(hasFolder ? { onChangeFolder: () => void ws.changeFolder() } : {})} />;
  const choose = () => void ws.choose();

  switch (phase.kind) {
    case 'checking':
      return <>{header}<Loading /></>;
    case 'none':
      return <>{header}<Setup viewer={viewer} install={install} onSignIn={() => signIn()} folder={{ refused: phase.refused, onChoose: choose }} /></>;
    case 'downloading':
      return <>{header}<Setup viewer={viewer} install={install} onSignIn={() => signIn()}
        folder={{ onChoose: choose, download: { folderName: phase.folderName, ...phase.progress } }} /></>;
    case 'reconnect':
      return <>{header}<ReconnectBanner folderName={phase.folderName} onReconnect={() => ws.reconnect()} /><Loading /></>;
    case 'gone':
      // choose() must start inside the click: Chrome only opens the picker on a user gesture.
      return <>{header}<FolderGone onChooseAgain={choose} /></>;
    case 'locked':
      return <>{header}<OtherWindow onUseThisWindow={() => void ws.useThisWindow()} /></>;
    case 'ready':
      return (
        <>
          {header}
          {route.name === 'files' && <FilesView phase={phase} ws={ws} />}
          {route.name === 'prs' && <PullRequests gh={gh} />}
          {route.name === 'pr' && <ReviewPage gh={gh} number={route.number} file={route.file} anchor={route.anchor} />}
        </>
      );
    case 'error':
      return <>{header}<ErrorScreen message={phase.message} onRetry={() => void ws.start()} /></>;
  }
}
